import { useState } from 'react'
import { Pencil } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { PageHeader } from '@/components/data/page-header'
import { PlanEditDialog } from '@/components/data/plan-edit-dialog'
import { MonoId } from '@/components/data/mono-id'
import { ErrorState, SkeletonRows } from '@/components/data/states'
import { formatMoney, formatNumber } from '@/lib/format'
import { usePlanList } from '@/lib/queries'
import type { Plan } from '@/lib/types'

export default function PlansPage() {
  const { data, error, isPending, refetch } = usePlanList()
  const [editing, setEditing] = useState<Plan | null>(null)
  const unlimited = data?.unlimited ?? 1_000_000
  const cap = (n: number | undefined) => (n === undefined ? '—' : n >= unlimited ? '∞' : formatNumber(n))

  return (
    <>
      <PageHeader title="Plans">Prices, features and limits. Plans can be edited here but not added or removed.</PageHeader>

      <div className="panel overflow-hidden">
        {error ? (
          <ErrorState error={error} onRetry={() => refetch()} />
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent">
                  <TableHead>Plan</TableHead>
                  <TableHead>Price</TableHead>
                  <TableHead>Features</TableHead>
                  <TableHead>Limits</TableHead>
                  <TableHead>Stripe price</TableHead>
                  <TableHead className="w-0" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {isPending ? (
                  <SkeletonRows columns={6} />
                ) : (
                  data.plans.map((p) => (
                    <TableRow key={p.id}>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{p.name}</span>
                          {!p.public && <Badge variant="secondary">Not for sale</Badge>}
                        </div>
                        <MonoId value={p.id} />
                      </TableCell>
                      <TableCell className="figures">
                        {p.price_cents ? `${formatMoney(p.price_cents)} / ${p.billing_type === 'yearly' ? 'year' : 'month'}` : '—'}
                      </TableCell>
                      <TableCell className="min-w-56 max-w-80 whitespace-normal text-muted-foreground">
                        {p.features.includes('*') ? 'Everything' : p.features.map((f) => f.replace(/_/g, ' ')).join(', ') || 'None'}
                      </TableCell>
                      <TableCell className="figures whitespace-normal text-muted-foreground">
                        <div>{cap(p.limits.companies)} companies</div>
                        <div>{cap(p.limits.users)} members</div>
                        <div>{cap(p.limits.integrations)} integrations</div>
                      </TableCell>
                      <TableCell>
                        {p.group === 'free' || (!p.stripe_price_id && p.group === 'opensource') ? (
                          <span className="text-muted-foreground">—</span>
                        ) : p.stripe_price_id ? (
                          <MonoId value={p.stripe_price_id} />
                        ) : (
                          <span className="text-warn">Not set</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" onClick={() => setEditing(p)}>
                          <Pencil /> Edit
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        )}
      </div>

      {editing && data && (
        <PlanEditDialog key={editing.id} plan={editing} allFeatures={data.all_features ?? []} unlimited={data.unlimited ?? 1_000_000} open onOpenChange={(o) => !o && setEditing(null)} />
      )}
    </>
  )
}
